/**
 * Credit economics for the one-time pack ladder.
 *
 * Dollar costs below are assumptions and are PENDING CONFIRMATION.
 * Seedance 2.5 rates are the public BytePlus / OpenRouter on-demand
 * prices (no reference video, 16:9, audio included). The KIE credit
 * price is the common billing-UI rate. Changing these constants reprices
 * every model that goes through `creditsFromProviderCostUsd`, except
 * Seedance 2.5, which uses the explicit per-second credit rates so a
 * pack still buys the anchor clip.
 */
export const CREDIT_ECONOMICS = {
  /** PENDING CONFIRMATION. USD per output second, no reference video. */
  seedance25CostPerSecondUsd: {
    '480p': 0.1028,
    '720p': 0.2311,
  },
  /** PENDING CONFIRMATION. USD per KIE credit. */
  kieCreditCostUsd: 0.005,
  /** Target gross margin used to turn provider cost into a retail price. */
  targetGrossMargin: 0.7,
  /** Retail value of one site credit. Matches the $9.90 / 50 pack. */
  creditValueUsd: 0.19,
  /** Seedance 2.5 maximum. Treated as a full-length clip. */
  fullLengthSeconds: 30,
  /**
   * Site credits per output second. 480p × 15s = 50 and 720p × 30s = 160,
   * so the three packs line up with one anchor video each.
   */
  seedance25CreditsPerSecond: {
    '480p': 10 / 3,
    '720p': 16 / 3,
  },
  /**
   * PENDING CONFIRMATION. WaveSpeed Grok video edit has no KIE dollar
   * rate in this repo. 65 was the old frontend credits/second; dividing
   * by the old economy multiplier estimates the provider credits.
   */
  grokVideoEditLegacyFrontendCreditsPerSecond: 65,
  legacyEconomyFrontendCreditsPerKieCredit: 6,
} as const;

/**
 * Existing subscriptions are not for sale. Renewal grants stay off until
 * a conversion ratio from the old credit unit is chosen. Do not turn this
 * on while `creditValueUsd` is ~$0.19 — old plans would mint thousands of
 * new-scale credits.
 */
export const LEGACY_SUBSCRIPTION_CREDIT_GRANTS_ENABLED = false;

/** Daily check-in is paused under the smaller credit unit. */
export const DAILY_CHECK_IN_ENABLED = false;

/**
 * Signup gift. Off: new accounts start at 0 credits and buy a pack before
 * their first generation. Every free-credit offer in the UI keys off this
 * being above 0.
 */
export const REGISTER_GIFT_CREDITS = 0;

export function creditsFromProviderCostUsd(costUsd: number): number {
  if (!Number.isFinite(costUsd) || costUsd <= 0) return 1;
  const margin = CREDIT_ECONOMICS.targetGrossMargin;
  const retailUsd = costUsd / (1 - margin);
  return Math.max(1, Math.round(retailUsd / CREDIT_ECONOMICS.creditValueUsd));
}

export function creditsFromKieCredits(kieCredits: number): number {
  return creditsFromProviderCostUsd(
    kieCredits * CREDIT_ECONOMICS.kieCreditCostUsd
  );
}

export function seedance25GenerationCredits({
  resolution,
  seconds,
}: {
  resolution: string;
  seconds: number;
}): number {
  const rates = CREDIT_ECONOMICS.seedance25CreditsPerSecond;
  const rate =
    resolution === '720p'
      ? rates['720p']
      : resolution === '480p'
        ? rates['480p']
        : undefined;
  if (rate === undefined) {
    throw new Error(
      `Seedance 2.5 has no credit rate for resolution "${resolution}".`
    );
  }
  if (!Number.isFinite(seconds) || seconds <= 0) return 1;
  return Math.max(1, Math.round(seconds * rate));
}
